import { hotkeys } from 'react-keyboard-shortcuts';
import { v4 as uuidv4 } from 'uuid';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ActionText from '../../core/ActionText';
import Errored from '../../core/Errored';
import Loading from '../../core/Loading';
import PagingFilter from '../../core/PagingFilter';
import ProjectLanguageSelector from '../../core/ProjectLanguageSelector';
import { ProjectConsumer } from '../../core/providers/ProjectProvider';
import { globalStyles } from '../../core/style/gstheme';
import { makeCancelable } from '../../core/utils/utils';
import { sortLabels } from '../utils/utils';
import GS from '../../grafsync/grafsync';
import LabelData from '../skosxl/LabelData';
import XLlabelDialogContainer from '../skosxl/XLlabelDialogContainer';
import XLstructure from '../skosxl/XLstructure';

const styles = Object.assign({}, globalStyles);

class LabelView extends Component {

  constructor(props) {
    super(props);
    this.state = {
      isLoading: false,
      initialSearch: true,
      labels: [],
      lang: this.props.defaultLang,
      text: "",
      searchID: null,
      page: 1,
      filterUnused: false,
      noneFound: false,
      noResults: true,
      pageCount: null,
      isErrored: null
    };
    this.infoRef = React.createRef();
    this.controlRef = React.createRef();
    this.cancellable = null;
  }

  hot_keys = {
    'alt+i': {
      priority: 1,
      handler: () => {
        if (this.infoRef && this.infoRef.current) {
          this.infoRef.current.focus();
        }
      }
    },
    'alt+c': {
      priority: 1,
      handler: () => {
        if (this.controlRef && this.controlRef.current) {
          this.controlRef.current.focus();
        }
      }
    }
  };

  componentDidMount() {
    this.handlePageChange(1, true);
  }

  handlePageRefresh = () => {
    this.setState({ text: "", initialSearch: true }, () => this.handlePageChange(this.state.page));
  };

  handleFilterChange = (text) => {
    this.setState({ text, initialSearch: text === "" }, () => this.handlePageChange(this.state.page, text === ""));
  };

  onSelectLanguage = (lang) => {
    this.setState({ lang, initialSearch: true }, () => this.handlePageChange(this.state.page, true));
  };

  /** We include 'fast' as a property to do the initial search without ordering
   * For very large sets of labels without this performance is too slow
   */
  handlePageChange = async (page = 1, initialSearch = false) => {
    if (!initialSearch && this.state.text.length < 2) return;
    this.setState(
      { isLoading: true },
      () => {
        let search = [{
          predicate: "http://www.w3.org/2004/02/skos/core#inScheme",
          value: this.props.schemeIRI
        }, {
          predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
          value: "http://www.w3.org/2008/05/skos-xl#Label"
        }];
        if (this.state.text !== "") {
          search.push({
            predicate: "http://www.w3.org/2008/05/skos-xl#literalForm",
            value: this.state.text,
            isExactMatch: false
          });
        }
        else {
          search.push({
            predicate: "http://www.w3.org/2008/05/skos-xl#literalForm"
          });
        }
        const searchID = uuidv4();
        this.setState({ searchID }, () => {
          this.doSearch({
            search: search,
            noCount: true,
            allowMissingResults: true,
            retainOrder: initialSearch,
            ...(initialSearch) && { fast: true },
            ...(this.state.filterUnused) && {
              excludeByPath: [
                {
                  predicate: [
                    "http://www.w3.org/2008/05/skos-xl#prefLabel"
                  ],
                  reverse: true
                },
                {
                  predicate: [
                    "http://www.w3.org/2008/05/skos-xl#altLabel"
                  ],
                  reverse: true
                },
                {
                  predicate: [
                    "http://www.w3.org/2008/05/skos-xl#hiddenLabel"
                  ],
                  reverse: true
                }
              ]
            },
            ...(this.state.filterUnused) && {
              excludeByPathOnly: true
            },
            graph: [this.props.graph],
            exclude: this.props.deletedLabels,
            language: this.state.lang,
            predicates: ["http://www.w3.org/2008/05/skos-xl#literalForm"],
            page: page,
            pageSize: 200,
            sort: ["http://www.w3.org/2008/05/skos-xl#literalForm"]
          }, searchID)
            .promise
            .then(data => {
              if (this.cancellable && this.cancellable.isCancelled()) return;
              // Ignore 'old' 
              if (data.uuid && data.uuid !== this.state.searchID) return;
              if (data.results && data.results.length > 0) {
                const sortedLabels = sortLabels(data.results);
                const labelIDs = sortedLabels.map(result => result.id);
                this.loadData(labelIDs, {})
                  .promise
                  .then(() => {
                    if (this.cancellable && this.cancellable.isCancelled()) return;
                    // Loading might take a while so check we haven't done another search in the meantime
                    if (data.uuid && data.uuid !== this.state.searchID) return;
                    this.setState({
                      labels: labelIDs,
                      page,
                      noResults: false,
                      noneFound: false,
                      isLoading: false
                    });
                  })
                  .catch((e) => {
                    if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
                  });
              }
              else {
                this.setState({
                  isLoading: false,
                  page: 1,
                  noneFound: true,
                  noResults: false
                });
              }
            })
            .catch((e) => {
              if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
        });
      });
  };

  static getDerivedStateFromProps(props, state) {
    if (!state.isLoading && props.updateAt !== state.refreshAt) {
      return {
        labels: null,
        noResults: true,
        initialSearch: true,
        refreshAt: props.updateAt
      };
    }
    return null;
  }

  componentDidUpdate() {
    if (!this.state.isLoading && this.state.labels === null && this.state.noResults === true) {
      this.handlePageChange(this.state.page, this.state.initialSearch);
    }
  }

  doSearch(payload, uuid) {
    this.cancellable = makeCancelable(
      GS.search(payload, uuid)
    );
    return this.cancellable;
  }

  loadData(iris, options) {
    this.cancellable = makeCancelable(
      GS.loadData(iris, options, this.props.graph)
    );
    return this.cancellable;
  }

  componentWillUnmount() {
    if (this.cancellable) this.cancellable.cancel();
  }

  handleChange = name => event => {
    this.setState({ [name]: event.target.checked }, () => this.handlePageChange(this.state.page, this.state.text === ""));
  };

  render() {

    const { isLoading, labels, text, isErrored, noneFound, lang } = this.state;
    const { activeLabel, deletedLabels, onSelect, onAddNewLabel, onAddExistingLabel, entitlements, useSlashIRI, useUuidIRI, useCounterIRI,
      onDeleteLabel, classes, schemeIRI, data, appBar, isReadOnly, graph, project, iriOptions, projectUsers, counters } = this.props;

    if (isErrored) return <Errored data={isErrored} />;

    return (
      <ProjectConsumer>
        {({ projectLang, largeDataSettings }) => (
          <div>
            <div className={classes.controlPanel}>
              <div className={classes.views}>
                {appBar}
              </div>
              <div className={classes.controlPanelInner}>
                <div className={classes.controlPanelAdd}>
                  {!isReadOnly &&
                    <XLlabelDialogContainer
                      id={schemeIRI}
                      useWorkflow={data.activateWorkflow === true}
                      iriOptions={iriOptions}
                      useSlashIRI={useSlashIRI}
                      useUuidIRI={useUuidIRI}
                      useCounterIRI={useCounterIRI}
                      counters={counters}
                      graph={graph}
                      addExistingLabel={onAddExistingLabel}
                      addNewLabel={onAddNewLabel} />
                  }
                  {isReadOnly && <div>&nbsp;</div>}
                </div>
                <div className={classes.controlPanelSearch}>
                  <PagingFilter
                    autoFocus={true}
                    lang={lang}
                    handleFilterChange={this.handleFilterChange}
                    text={text}
                    maxLength={50}
                    placeholder="Max 200 displayed. Filter to refine." />
                </div>
              </div>
              <div
                tabIndex={0}
                ref={this.controlRef}
                className={classes.tree}>
                {!largeDataSettings &&
                  <div className={classes.treeOptions}>
                    <FormControlLabel
                      classes={{ label: classes.formLabelInline }}
                      control={
                        <Switch
                          data-test="unused-labels"
                          checked={this.state.filterUnused}
                          onChange={this.handleChange('filterUnused')}
                          value="filterUnused"
                          color="primary"
                        />
                      }
                      label="Unused Labels Only"
                    />
                  </div>
                }
                {projectLang.length > 1 &&
                  <div data-test="label-language" style={{ marginLeft: 30, width: 'calc(100% - 40px)' }}>
                    <ProjectLanguageSelector includeLabel={false} onSelect={this.onSelectLanguage} />
                  </div>
                }
                {
                  isLoading ?
                    <Loading offset="100px" />
                    :
                    <XLstructure
                      labels={labels}
                      graph={graph}
                      useWorkflow={data.activateWorkflow === true}
                      schemeIRI={schemeIRI}
                      noneFound={noneFound}
                      activeLabel={activeLabel}
                      deletedLabels={deletedLabels}
                      id={schemeIRI}
                      onSelect={onSelect} />
                }
              </div>
            </div>
            <div
              ref={this.infoRef}
              tabIndex={0}
              data-test="info-panel"
              className={classes.infoPanel}>
              {activeLabel === null ?
                <ActionText offset="180px">Select a label to view the detail.</ActionText>
                :
                <LabelData
                  key={activeLabel}
                  project={project}
                  graph={graph}
                  expectedTypes={["Label"]}
                  projectUsers={projectUsers}
                  useWorkflow={data.activateWorkflow === true}
                  propertyTemplates={data.associatedTemplate}
                  annotate={data.activateAnnotate === true}
                  entitlements={entitlements}
                  linkedData={data.linkedData}
                  useSlashIRI={data.useSlashIRI || false}
                  associatedProject={data.associatedProject ?
                    Array.isArray(data.associatedProject) ?
                      data.associatedProject
                      :
                      [data.associatedProject]
                    :
                    []
                  }
                  schemeIRI={schemeIRI}
                  labelIRI={activeLabel}
                  onDeleteLabel={onDeleteLabel} />
              }
            </div>
          </div>
        )}
      </ProjectConsumer>
    );
  }

}

LabelView.propTypes = {
  appBar: PropTypes.node.isRequired,
  graph: PropTypes.string.isRequired,
  entitlements: PropTypes.array.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  schemeIRI: PropTypes.string.isRequired,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  useCounterIRI: PropTypes.bool,
  counters: PropTypes.object,
  projectUsers: PropTypes.array,
  data: PropTypes.object.isRequired,
  project: PropTypes.string.isRequired,
  onAddExistingLabel: PropTypes.func.isRequired,
  onAddNewLabel: PropTypes.func.isRequired,
  updateAt: PropTypes.number,
  activeLabel: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  onDeleteLabel: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  deletedLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
  classes: PropTypes.object,
  iriOptions: PropTypes.object
};

export default withStyles(styles)(hotkeys(LabelView));